import React from 'react';
import { View } from 'react-native';
import { SvgXml } from 'react-native-svg';
import { useTheme } from '@vidahealth/vida-ds';

// The Figma account-flow glyphs as exported, stroked with currentColor so they follow the theme.
const GLYPHS = {
  mail: {
    size: 24,
    xml: `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M22 7L13.009 12.727C12.7039 12.9042 12.3573 12.9976 12.0045 12.9976C11.6517 12.9976 11.3051 12.9042 11 12.727L2 7M4 4H20C21.1046 4 22 4.89543 22 6V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6C2 4.89543 2.89543 4 4 4Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
</g>
</svg>`,
  },
  'clock-alert': {
    size: 24,
    xml: `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M12 6V12L16 14M20 12V17M20 21H20.01M21.25 8.20002C20.4957 6.3614 19.2106 4.78922 17.5588 3.68415C15.9071 2.57908 13.9635 1.99123 11.9761 1.99563C9.98881 2.00003 8.04785 2.59648 6.40098 3.70886C4.75412 4.82124 3.47605 6.39909 2.72989 8.24104C1.98373 10.083 1.80332 12.1055 2.21169 14.0504C2.62005 15.9954 3.59868 17.7745 5.02265 19.1608C6.44663 20.5471 8.25138 21.4777 10.2066 21.8338C12.1617 22.1899 14.1787 21.9553 16 21.16" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
</g>
</svg>`,
  },
  check: {
    size: 24,
    xml: `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M20 6L9 17L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
</g>
</svg>`,
  },
  'message-square': {
    size: 24,
    xml: `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M22 17C22 17.5304 21.7893 18.0391 21.4142 18.4142C21.0391 18.7893 20.5304 19 20 19H6.828C6.29761 19.0001 5.78899 19.2109 5.414 19.586L3.212 21.788C3.1127 21.8873 2.9862 21.9549 2.84849 21.9823C2.71077 22.0097 2.56803 21.9956 2.43831 21.9419C2.30858 21.8881 2.1977 21.7971 2.11969 21.6804C2.04167 21.5637 2.00002 21.4264 2 21.286V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H20C20.5304 3 21.0391 3.21071 21.4142 3.58579C21.7893 3.96086 22 4.46957 22 5V17Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
</g>
</svg>`,
  },
  'circle-alert': {
    size: 16,
    xml: `<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M8 5.33333V8M8 10.6667H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8Z" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
</g>
</svg>`,
  },
} as const;

type GlyphName = keyof typeof GLYPHS;

export const Glyph: React.FC<{ name: GlyphName; color: string }> = ({ name, color }) => {
  const { size, xml } = GLYPHS[name];
  return <SvgXml xml={xml} width={size} height={size} color={color} />;
};

// The Figma's 56px badge over a confirmation or code step: the info colour at 10% behind the glyph.
export const GlyphBadge: React.FC<{ name: GlyphName; testID?: string }> = ({ name, testID }) => {
  const { colors } = useTheme();
  return (
    <View
      className='mb-ds-16 h-[56px] w-[56px] items-center justify-center overflow-hidden rounded-full'
      testID={testID}
    >
      <View className='absolute inset-0 bg-status-info opacity-10' />
      <Glyph name={name} color={colors['color-text-primary']} />
    </View>
  );
};
